import './DynamicProfile.scss';
import { Extent, Map, MapView, SpatialReference } from 'cd-eft/utils/esriTypes';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DynamicProfileStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileStoreBase';
import stores from 'cd-eft/models/Stores';
import { Row, Col } from 'reactstrap';
import { mdiCircleMedium, mdiCrosshairsGps, mdiFormatListBulleted } from '@mdi/js';
import Icon from '@mdi/react';
import { Loader } from '../LoadingSpinner/LoadingSpinner';

const libraryStore = stores.AppStore.projectsLibraryStore;

interface IDynamicWaterwayProfileProps {
  profileStore: DynamicProfileStoreBase | undefined;
  editable: boolean;
  generateInitialProfiles;
  bankSelection: 'showMergedLowBank' | 'showLeftRightBanks';
  uiReady: boolean;
}

const DynamicWaterwayProfile = (props: IDynamicWaterwayProfileProps) => {
  const [waitingInit, setWaitingInit] = useState(true);
  const [legendVisible, setLegendVisible] = useState(true);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const profileMapViewRef = useRef<MapView>();

  const startCoordinateReadout = useCallback(() => {
    if (profileMapViewRef.current) {
      props.profileStore?.startCoordinateReadout();
    }
  }, [props.profileStore]);

  const stopCoordinateReadout = useCallback(() => {
    if (props.profileStore) {
      props.profileStore.stopCoordinateReadout();
    }
  }, []);

  const toggleCoordinates = () => {
    setShowCoordinates(prevState => !prevState);
  }

  useEffect(() => {
    if (showCoordinates) {
      startCoordinateReadout();
    } else {
      stopCoordinateReadout();
    }
  }, [showCoordinates]);

  if (!profileMapViewRef.current) {
    const spatialReference = new SpatialReference({ wkid: 102965 });
    const extent = new Extent({ xmin: 0, ymin: 0, xmax: 100, ymax: 210, spatialReference });

    profileMapViewRef.current = new MapView({ map: new Map({ layers: [] }), extent, spatialReference });
  }

  useEffect(() => {
    if (profileMapViewRef.current && props.uiReady) {

      const sr: SpatialReference = libraryStore.projectSpatialReference!;
      props.profileStore?.initialize(profileMapViewRef.current, sr, props.editable, 'waterway');
      setWaitingInit(false);

      profileMapViewRef.current.container = document.getElementById('waterway-Profile') as HTMLDivElement;
      if (props.profileStore) props.profileStore.startLengthyProcess = (state: boolean) => setWaitingInit(state)
      props.generateInitialProfiles();
    }
  }, [props.editable, props.profileStore, props.uiReady]);

  return (
    <div id={'profile-and-peripherals-container'}>
      <div id={'waterway-Profile'} style={{ position: "relative" }}>
        <div id="waterway-profile-coordinates-container"></div>
        <div className={`profile-legend-container ${legendVisible ? 'visible' : 'hidden'}`} role="region" aria-label="WDT Legend">
          <Row>
            <Col className="col-7">Centerline</Col>
            <Col><div className="dashedline-blue" /></Col>
          </Row>
          <Row>
            <Col className="col-7">Reach</Col>
            <Col><div className="solidline" /></Col>
          </Row>
          {props.bankSelection === 'showLeftRightBanks' ? (
            <>
              <Row>
                <Col className="col-7">Left Bank</Col>
                <Col><div className="dottedline-red" /></Col>
              </Row>
              <Row>
                <Col className="col-7">Right Bank</Col>
                <Col><div className="dottedline-green" /></Col>
              </Row>
            </>
          ) : (
            <Row>
              <Col className="col-7">Low Bank</Col>
              <Col><div className="dottedline-pink" /></Col>
            </Row>
          )}

          <Row>
            <Col className="col-7">Control Limits</Col>
            <Col className="d-flex">
              <div className="doubleline-red" />
              <div className="doubleline-red" />
            </Col>
          </Row>
          <Row>
            <Col className="col-7">Reach Station</Col>
            <Col><Icon path={mdiCircleMedium} size={1} /></Col>
          </Row>
          <Row>
            <Col className="col-7">Editable Station</Col>
            <Col><Icon path={mdiCircleMedium} size={1} className="editable-station" /></Col>
          </Row>
        </div>
        <div className="mt-5" hidden={!waitingInit}>
          <Loader message={'Loading profiles ...'} />
        </div>
      </div>
      <div className="profile-esri-btns">
          <div title='Legend' className='esri-widget--button esri-widget esri-interactive home profile-btn' onClick={() => setLegendVisible(!legendVisible)}><Icon path={mdiFormatListBulleted} size={0.9} horizontal/></div>
          <div title='Show Coordinates' className='esri-widget--button esri-widget esri-interactive home profile-btn' onClick={toggleCoordinates}><Icon path={mdiCrosshairsGps} size={0.8} /></div>
          <div title='Zoom In' className='esri-icon-zoom-in-fixed esri-widget--button esri-widget esri-interactive home profile-btn' onClick={() => { if (profileMapViewRef.current) profileMapViewRef.current.extent = profileMapViewRef.current.extent.expand(0.75) }}></div>
          <div title='Zoom Out' className='esri-icon-zoom-out-fixed esri-widget--button esri-widget esri-interactive home profile-btn' onClick={() => { if (profileMapViewRef.current) profileMapViewRef.current.extent = profileMapViewRef.current.extent.expand(1.25) }}></div>
          <div title='Zoom to Extent' className='esri-icon-applications esri-widget--button esri-widget esri-interactive home profile-btn' onClick={() => { if (profileMapViewRef.current && props.profileStore?.maxExtent) profileMapViewRef.current.extent = props.profileStore?.maxExtent }}></div>
      </div>
    </div>
  );
};

export { DynamicWaterwayProfile };
